import React, { useState } from "react";
import {
    Container,
    Typography,
    TextField,
    Button,
    CssBaseline,
    Grid,
    Link,
    CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { API, Auth } from "aws-amplify";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import VerificationForm from "./VerificationForm";
import Logo from "../../assets/logo.jpg";

const useStyles = makeStyles({
    root: {
        display: "flex",
        height: "100vh",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(to right, #0a0068, #cc4049)",
    },
    content: {
        textAlign: "center",
        color: "#ffffff",
    },
    loginForm: {
        background: "#fff",
        padding: "40px",
        height: "auto",
        borderRadius: "8px",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
    },
    submit: {
        marginTop: "16px",
        backgroundColor: "#0a0068",
        color: "#ffffff",
    },
    errorMessage: {
        color: "red",
        textAlign: "center",
    },
});

const api_name = process.env.REACT_APP_STAGE === "DEV" ? process.env.REACT_APP_API_NAME_DEV : process.env.REACT_APP_API_NAME_PROD;

const LoginPage = ({ onRefresh }) => {
    const classes = useStyles();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmationCode, setConfirmationCode] = useState("");
    const [resetPassword, setResetPassword] = useState(false);
    const [resetStage, setResetStage] = useState(1);
    const [showFirstTime, setShowFirstTime] = useState(false);
    const [userChallenge, setUserChallenge] = useState(null);
    const [emailConfirmed, setEmailConfirmed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const navigate = useNavigate();

    const fetchAndStoreUserData = async () => {
        const apiName = api_name;
        const path = ""; // your backend path
        const params = {
            body: {
                command: "getUser",
                email,
            },
            response: true,
            convertEmptyValues: true,
        };

        try {
            const response = await API.post(apiName, path, params);
            const result = JSON.parse(response.data.body.body);

            if (response.data.statusCode === 200) {
                localStorage.setItem("user", email);
                localStorage.setItem("userData", JSON.stringify(result));
                localStorage.setItem("fname", result.fname);
                localStorage.setItem("lname", result.lname);
                localStorage.setItem("userRole", result.user_role);
                localStorage.setItem("profile_image", result.profile_image);
                localStorage.setItem("linkedin_url", result.linkedin_url);
                localStorage.setItem("git_url", result.git_url);
                localStorage.setItem("experience_level", result.experience_level);

                onRefresh()
                getDefaultPath(result.user_role);
            } else {
                setErrorMessage(result.message);
            }
        } catch (error) {
            console.error(error);
            setErrorMessage("Failed to fetch user details.");
        }
    };
    const getDefaultPath = (role) => {
        switch (role) {
            case 'Mentor':
                return navigate("/Dashboard"); // Mentors start at Dashboard
            case 'Client':
                return navigate('/Dashboard'); // Clients start at Dashboard
            case 'User':
                return navigate('/AssessmentIndex'); // Users start at Assessments
            default:
                return navigate('/Dashboard'); // Fallback to Dashboard
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        setErrorMessage("");

        try {
            const user = await Auth.signIn(email, password);
            if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
                setUserChallenge(user);
            } else {
                await fetchAndStoreUserData();
            }
        } catch (err) {
            if (err.code === "UserNotConfirmedException") {
                setShowFirstTime(true);
                setErrorMessage("Email not confirmed. Please confirm below.");
            } else {
                setErrorMessage(err.message || "Login failed");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleNewPassword = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        try {
            await Auth.completeNewPassword(userChallenge, newPassword);
            alert("Password updated. Please log in again.");
            setUserChallenge(null);
            setPassword("");
        } catch (err) {
            setErrorMessage(err.message || "Failed to set new password");
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmEmail = async (code, emailParam) => {
        setIsLoading(true);
        setErrorMessage("");
        try {
            await Auth.confirmSignUp(emailParam, code);
            setEmailConfirmed(true);
        } catch (err) {
            setErrorMessage(err.message || "Confirmation failed");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = async (emailParam) => {
        try {
            await Auth.resendSignUp(emailParam);
            alert("Confirmation code resent to your email.");
            setEmail(emailParam);
        } catch (err) {
            setErrorMessage(err.message || "Failed to resend code");
        }
    };

    const handleResetPasswordRequest = async () => {
        setIsLoading(true);
        setErrorMessage("");
        try {
            await Auth.forgotPassword(email);
            alert("Verification code sent to your email.");
            setResetStage(2);
        } catch (err) {
            setErrorMessage(err.message || "Failed to request password reset");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPasswordConfirm = async () => {
        setIsLoading(true);
        setErrorMessage("");
        try {
            await Auth.forgotPasswordSubmit(email, confirmationCode, newPassword);
            alert("Password reset successful. You can now login.");
            setResetPassword(false);
            setResetStage(1);
            setConfirmationCode("");
            setNewPassword("");
        } catch (err) {
            setErrorMessage(err.message || "Failed to reset password");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={classes.root}>
            <CssBaseline />
            <Grid container justifyContent="center" alignItems="center" spacing={2}>
                <Grid item xs={12} md={6}>
                    <Container maxWidth="sm" className={classes.content}>
                        <Typography variant="h4" className={classes.typingText}>Welcome to <span style={{ fontWeight: 900 }}>Career Excellence</span></Typography>
                    </Container>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Container maxWidth="xs" className={classes.content}>
                        <div className={classes.loginForm}>
                            <img src={Logo} alt="Logo" style={{ height: '75px', marginBottom: '20px' }} />
                            <Typography
                                component="h1"
                                variant="h6"
                                align="center"
                                style={{
                                    color: "#000000",
                                    marginBottom: "20px",
                                    fontWeight: 700,
                                }}
                            >
                                {showFirstTime
                                    ? "Confirm Your Email"
                                    : resetPassword
                                        ? "Reset Your Password"
                                        : "Login to your Account"}
                            </Typography>

                            {/* ==== LOGIN / NEW PASSWORD ==== */}
                            {!showFirstTime && !resetPassword && (
                                <form onSubmit={userChallenge ? handleNewPassword : handleSubmit}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label={userChallenge ? "New Password" : "Password"}
                                        type="password"
                                        value={userChallenge ? newPassword : password}
                                        onChange={(e) =>
                                            userChallenge
                                                ? setNewPassword(e.target.value)
                                                : setPassword(e.target.value)
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                    <Typography variant="body2" className={classes.errorMessage}>
                                        {errorMessage}
                                    </Typography>
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        className={classes.submit}
                                        disabled={isLoading}
                                        startIcon={
                                            isLoading ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : null
                                        }
                                    >
                                        {isLoading
                                            ? userChallenge
                                                ? "Setting Password..."
                                                : "Signing In..."
                                            : userChallenge
                                                ? "Set New Password"
                                                : "Sign In"}
                                    </Button>

                                    <Grid container justifyContent="space-between" sx={{ mt: 2 }}>
                                        {/* <Grid item>
                                            <Link component={RouterLink} to="/signup" variant="body2">
                                                Don't have an account? Sign Up
                                            </Link>
                                        </Grid> */}
                                        <Grid item sx={{ mt: 2 }}>
                                            <Link
                                                component="button"
                                                variant="body2"
                                                onClick={() => setResetPassword(true)}
                                            >
                                                Forgot Password?
                                            </Link>
                                        </Grid>
                                        {/* <Grid item sx={{ mt: 2 }}>
                                            <Link
                                                component="button"
                                                variant="body2"
                                                onClick={() => setShowFirstTime(true)}
                                            >
                                                First time login? Confirm your email
                                            </Link>
                                        </Grid> */}
                                    </Grid>
                                </form>
                            )}

                            {/* ==== CONFIRM EMAIL ==== */}
                            {showFirstTime && (
                                <VerificationForm
                                    email={email}
                                    onConfirm={handleConfirmEmail}
                                    onResend={handleResendCode}
                                    errorMessage={errorMessage}
                                    showEmailField={true}
                                    loading={isLoading}
                                    success={emailConfirmed}
                                />
                            )}

                            {/* ==== RESET PASSWORD ==== */}
                            {resetPassword && (
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        resetStage === 1
                                            ? handleResetPasswordRequest()
                                            : handleResetPasswordConfirm();
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                    {resetStage === 2 && (
                                        <>
                                            <TextField
                                                fullWidth
                                                margin="normal"
                                                label="Verification Code"
                                                value={confirmationCode}
                                                onChange={(e) => setConfirmationCode(e.target.value)}
                                                required
                                            />
                                            <TextField
                                                fullWidth
                                                margin="normal"
                                                label="New Password"
                                                type="password"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                required
                                            />
                                        </>
                                    )}
                                    <Typography variant="body2" className={classes.errorMessage}>
                                        {errorMessage}
                                    </Typography>
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        className={classes.submit}
                                        disabled={isLoading}
                                        startIcon={
                                            isLoading ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : null
                                        }
                                    >
                                        {isLoading
                                            ? "Please wait..."
                                            : resetStage === 1
                                                ? "Send Code"
                                                : "Reset Password"}
                                    </Button>

                                    <Grid container justifyContent="center" sx={{ mt: 2 }}>
                                        <Grid item>
                                            <Link
                                                component="button"
                                                variant="body2"
                                                onClick={() => setResetPassword(false)}
                                            >
                                                Back to Sign In
                                            </Link>
                                        </Grid>
                                    </Grid>
                                </form>
                            )}
                        </div>
                    </Container>
                </Grid>
            </Grid>
        </div>
    );
};

export default LoginPage;
